<template>
  <v-container fluid class="partners-register py-8">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8" xl="7">
        <!-- Header -->
        <v-card class="mb-6 rounded-lg elevation-3" color="primary" dark>
          <v-card-text class="d-flex align-center py-6">
            <v-avatar color="white" size="56" class="mr-4 flex-shrink-0">
              <v-icon color="primary" size="30">mdi-account-multiple-plus</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">Registro de Partners</div>
              <div class="text-body-2" style="opacity: 0.85">
                {{ inviterData ? inviterData.tournament_name : 'Torneo de Golf' }}
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Loading State -->
        <v-card v-if="loading" class="mb-6 rounded-lg elevation-2">
          <v-card-text class="text-center py-12">
            <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
            <p class="grey--text mt-4 mb-0">Cargando información del Jugador...</p>
          </v-card-text>
        </v-card>

        <!-- Error State -->
        <v-alert
          v-if="error"
          type="error"
          border="left"
          colored-border
          elevation="2"
          dismissible
          class="mb-6 rounded-lg"
          @input="error = null"
        >
          {{ error }}
        </v-alert>

        <!-- Success Message -->
        <v-alert
          v-if="successMessage"
          type="success"
          border="left"
          colored-border
          elevation="2"
          dismissible
          class="mb-6 rounded-lg"
          @input="successMessage = null"
        >
          {{ successMessage }}
        </v-alert>

        <!-- Main Content -->
        <v-row v-if="!loading && inviterData">
          <!-- Left Panel: Inviter Data -->
          <v-col cols="12" md="6">
            <v-card class="rounded-lg elevation-2 fill-height">
              <v-card-title class="subtitle-1 font-weight-bold">
                <v-icon color="primary" class="mr-2">mdi-account-circle-outline</v-icon>
                Información del Jugador
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text class="pa-6">
                <div class="text-caption font-weight-bold mb-1 grey--text">NOMBRE</div>
                <div class="text-body1 font-weight-medium mb-5">{{ inviterData.full_name }}</div>

                <v-row dense class="mb-1">
                  <v-col cols="12" sm="6">
                    <div class="text-caption font-weight-bold mb-1 grey--text">EMAIL</div>
                    <div class="text-body2 text-truncate">{{ inviterData.email }}</div>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="text-caption font-weight-bold mb-1 grey--text">TELÉFONO</div>
                    <div class="text-body2">{{ inviterData.phone }}</div>
                  </v-col>
                </v-row>

                <v-divider class="my-5"></v-divider>

                <div class="text-caption font-weight-bold mb-2 grey--text">CÓDIGO DE ACCESO</div>
                <v-chip color="primary" text-color="white" class="font-weight-medium mb-5">
                  <v-icon left small>mdi-key-variant</v-icon>
                  {{ inviterData.access_code }}
                </v-chip>

                <v-divider class="my-5"></v-divider>

                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="text-caption font-weight-bold grey--text">CUPO DE INVITADOS</span>
                  <span class="text-caption font-weight-bold">{{ inviterData.guest_count }}/{{ maxGuests }}</span>
                </div>
                <v-progress-linear
                  :value="(inviterData.guest_count / maxGuests) * 100"
                  :color="inviterData.guests_limit_reached ? 'error' : 'primary'"
                  height="8"
                  rounded
                ></v-progress-linear>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Panel: Guests Section -->
          <v-col cols="12" md="6">
            <!-- Add Guest Form -->
            <v-card class="mb-6 rounded-lg elevation-2">
              <v-card-title class="subtitle-1 font-weight-bold">
                <v-icon color="primary" class="mr-2">mdi-account-plus-outline</v-icon>
                Agregar Invitados
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text class="pa-6">
                <p class="text-caption grey--text mb-4" v-if="!inviterData.guests_limit_reached">
                  Puedes agregar {{ inviterData.remaining_guests }} invitado{{ inviterData.remaining_guests !== 1 ? 's' : '' }} más
                </p>
                <p v-else class="text-caption error--text mb-4 font-weight-bold">
                  <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
                  Has completado tus {{ maxGuests }} invitados
                </p>

                <v-form ref="guestForm" @submit.prevent="addGuest">
                  <v-text-field
                    v-model="newGuestName"
                    label="Nombre del Invitado"
                    placeholder="Ej: Juan Pérez López"
                    outlined
                    dense
                    hide-details="auto"
                    :disabled="isAddingGuest || inviterData.guests_limit_reached"
                    @keyup.enter="addGuest"
                    class="mb-4"
                  ></v-text-field>

                  <v-btn
                    type="submit"
                    color="primary"
                    block
                    depressed
                    :loading="isAddingGuest"
                    :disabled="!newGuestName.trim() || inviterData.guests_limit_reached || isAddingGuest"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Agregar Invitado
                  </v-btn>
                </v-form>
              </v-card-text>
            </v-card>

            <!-- Guests List -->
            <v-card class="rounded-lg elevation-2">
              <v-card-title class="subtitle-1 font-weight-bold">
                <v-icon color="primary" class="mr-2">mdi-account-multiple-outline</v-icon>
                <span>Invitados</span>
                <v-spacer></v-spacer>
                <v-chip small color="primary" text-color="white">
                  {{ guestCount }}/{{ maxGuests }}
                </v-chip>
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text class="pa-6">
                <div v-if="guestCount === 0" class="text-center py-8">
                  <v-icon large color="grey lighten-1" class="mb-3">mdi-account-multiple-outline</v-icon>
                  <p class="grey--text mb-0">Aún no has agregado invitados</p>
                </div>

                <v-list v-else class="py-0">
                  <v-list-item v-for="guest in guests" :key="guest.id" class="guest-item mb-2">
                    <v-list-item-avatar color="primary" size="40">
                      <span class="white--text text-body2 font-weight-bold">{{ getInitials(guest.full_name) }}</span>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">{{ guest.full_name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="guest.created_at">
                        Agregado el {{ formatDateTime(guest.created_at) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Not Found State -->
        <v-card v-if="!loading && !inviterData && !error" class="rounded-lg elevation-2">
          <v-card-text class="pa-8 text-center">
            <v-icon large color="grey lighten-1" class="mb-3">mdi-alert-circle-outline</v-icon>
            <p class="subtitle-1 grey--text mb-1">No se encontraron datos del Jugador</p>
            <p class="text-caption grey--text mb-0">Verifica que el token de invitación sea válido</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { partnersRegisterAPI } from '@/services/partnersRegister'

export default {
  name: 'PartnersRegister',
  data () {
    return {
      inviterData: null,
      guests: [],
      newGuestName: '',
      loading: true,
      isAddingGuest: false,
      error: null,
      successMessage: null,
      inviterToken: null
    }
  },
  computed: {
    guestCount () {
      return this.guests.length
    },
    // El máximo lo define cada torneo (max_guests_per_member); 3 era el valor fijo anterior
    maxGuests () {
      return (this.inviterData && this.inviterData.max_guests) || 3
    }
  },
  mounted () {
    this.inviterToken = this.$route.params.inviter_token
    if (this.inviterToken) {
      this.fetchInviterData()
    } else {
      this.error = 'Token de invitación no válido'
      this.loading = false
    }
  },
  methods: {
    async fetchInviterData () {
      try {
        this.loading = true
        this.error = null
        const response = await partnersRegisterAPI.getInviterData(this.inviterToken)
        this.inviterData = response.data
        this.guests = response.data.guests || []
      } catch (err) {
        if (err.response?.status === 404) {
          this.error = 'Token de invitación inválido o miembro no encontrado'
        } else if (err.response?.status === 400) {
          this.error = err.response.data.error || 'Parámetros inválidos'
        } else {
          this.error = 'Error al cargar los datos del jugador. Por favor, intenta de nuevo.'
        }
      } finally {
        this.loading = false
      }
    },

    async addGuest () {
      if (!this.newGuestName.trim()) {
        this.error = 'Por favor ingresa el nombre del invitado'
        return
      }

      if (this.inviterData.guests_limit_reached) {
        this.error = 'Ya has alcanzado el límite de invitados permitidos'
        return
      }

      try {
        this.isAddingGuest = true
        this.error = null
        const response = await partnersRegisterAPI.addGuest(this.inviterToken, this.newGuestName)

        if (response.data.data) {
          this.guests.push(response.data.data)
          this.inviterData.guest_count += 1
          this.inviterData.remaining_guests -= 1
          if (this.inviterData.guest_count >= this.maxGuests) {
            this.inviterData.guests_limit_reached = true
          }
          this.successMessage = `"${this.newGuestName}" ha sido agregado exitosamente`
          this.newGuestName = ''
        }
      } catch (err) {
        if (err.response?.status === 400) {
          const errorData = err.response.data
          if (errorData.error === 'Límite de invitados alcanzado') {
            this.error = errorData.message || 'Ya alcanzaste el máximo de invitados. No puedes agregar más'
            this.inviterData.guests_limit_reached = true
            this.inviterData.remaining_guests = 0
          } else if (errorData.error?.includes('requerido')) {
            this.error = 'Por favor completa los campos requeridos'
          } else {
            this.error = errorData.error || 'Error al agregar el invitado'
          }
        } else if (err.response?.status === 404) {
          this.error = 'Token inválido o miembro no encontrado'
        } else {
          this.error = 'Error al agregar el invitado. Por favor intenta de nuevo.'
        }
      } finally {
        this.isAddingGuest = false
      }
    },

    getInitials (fullName) {
      if (!fullName) return '?'
      return fullName
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    },

    formatDateTime (dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.partners-register {
  max-width: 100%;
}

.guest-item {
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.02);
}

.guest-item:hover {
  background: rgba(0, 0, 0, 0.04);
}
</style>
